'use client';

/**
 * StarterGrid — 2x2 topic-prompt grid shown in the unified v3 empty state
 * (design turn 6): both a brand-new visit (UIState.LANDING) and a fresh chat
 * started via "New" with nothing sent yet (UIState.CHAT, zero messages)
 * render this same grid — there's no more separate landing-only topic-card
 * component. Reuses the shared ASSISTANT_TOPICS data source and onSelect
 * contract, capped to the first 4 topics.
 */

import { memo, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { V3_COLORS } from '@/lib/design-tokens';
import { ASSISTANT_TOPICS, type AssistantTopic } from '@/lib/assistant-topics';

const GRID_TOPICS = ASSISTANT_TOPICS.slice(0, 4);

export interface StarterGridProps {
  onSelect: (topic: AssistantTopic) => void;
  className?: string;
}

export const StarterGrid = memo(function StarterGrid({
  onSelect,
  className,
}: StarterGridProps) {
  return (
    <div
      className={cn(
        'mx-auto grid w-full max-w-[580px] grid-cols-2 gap-3.5 px-[16px]',
        className
      )}
    >
      {GRID_TOPICS.map((topic) => (
        <StarterCard key={topic.label} topic={topic} onSelect={onSelect} />
      ))}
    </div>
  );
});

const StarterCard = memo(function StarterCard({
  topic,
  onSelect,
}: {
  topic: AssistantTopic;
  onSelect: (topic: AssistantTopic) => void;
}) {
  const Icon = topic.icon;
  const handleClick = useCallback(() => onSelect(topic), [topic, onSelect]);

  return (
    <button
      onClick={handleClick}
      // Border-color transition (hover) merged with the shared press feel's
      // transform — can't just append PRESSABLE, see its doc comment in
      // design-tokens.ts.
      // Mobile sizes are tighter (icon 36px, 14px label, 14px padding): at
      // 390px each card is ~156px wide and the desktop metrics leave the
      // label ~62px — narrower than the single word "Sudarshan", which then
      // spills past the card border (FB26).
      className="flex items-center gap-[10px] rounded-[14px] border-[1px] bg-white px-[14px] py-[14px] text-left transition-[border-color,transform] duration-[160ms] ease-out hover:border-[#e36001]/25 hover:shadow-[0_2px_12px_rgba(227,96,1,0.08)] motion-safe:active:scale-[0.96] sm:gap-[12px] sm:px-[19px] sm:py-[17px]"
      style={{ borderColor: V3_COLORS.warmBorderAlt }}
    >
      <span
        className="flex size-9 shrink-0 items-center justify-center rounded-full sm:size-11"
        style={{ backgroundColor: '#FCE7D2' }}
      >
        <Icon
          className="size-5"
          style={{ color: V3_COLORS.orange }}
          strokeWidth={2}
        />
      </span>
      <span
        className="min-w-0 text-[14px] font-semibold leading-snug sm:text-[15.5px]"
        style={{ color: V3_COLORS.ink }}
      >
        {topic.label}
      </span>
    </button>
  );
});
